import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { formatPdpSelectionLabel } from '../../../shared/utilities/pdpSelectionProgress';
import { setHideHeaderMenuOnScroll as setHideHeaderMenuOnScrollAction } from '../../../redux/actions/elements';
import styles from './PdpStickySelectionHeader.module.css';

const backArrow = require('./images/back-arrow-brand-refresh.svg');

/** Align with BR PDP sticky CTA / layout breakpoint (max tablet = 991px). */
const MOBILE_MEDIA_QUERY = '(max-width: 991px)';
/** Ignore tiny scroll jitter from layout reflows while selecting cookies. */
const SCROLL_DIRECTION_THRESHOLD_PX = 12;

const getIsMobileViewport = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(MOBILE_MEDIA_QUERY).matches;
};

/**
 * Measure fixed site chrome so sticky sits underneath (cart / location stay usable).
 */
export const getSiteHeaderOffset = () => {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return 0;
  }

  const banner = document.querySelector('[role="banner"]');
  if (banner) {
    return Math.ceil(banner.getBoundingClientRect().height);
  }

  // CSS-modules hash includes "headerWrapper" (e.g. Header_headerWrapper__abc).
  const headerWrapper = document.querySelector('[class*="headerWrapper"]');
  if (headerWrapper) {
    return Math.ceil(headerWrapper.getBoundingClientRect().height);
  }

  return 0;
};

/**
 * Mobile sticky header for Box / Customizable PDPs (Figma 8427:38567).
 * Appears only after the product title has been seen and then scrolled out of view.
 * Anchored below the site header so primary nav / cart remain reachable — unless the
 * Menu-style scroll behavior hides the top nav while this banner is visible.
 *
 * Optional Redux prop overrides are for unit tests; production uses hooks.
 */
const PdpStickySelectionHeader = ({
  productName,
  selectedCount,
  requiredCount,
  onBack,
  observeTargetRef,
  observeKey,
  hideHeaderMenuOnScroll: hideHeaderMenuOnScrollProp,
  setHideHeaderMenuOnScroll: setHideHeaderMenuOnScrollProp,
}) => {
  const dispatch = useDispatch();
  const hideHeaderMenuOnScrollFromStore = useSelector(
    state => state.elements.hideHeaderMenuOnScroll
  );
  const hideHeaderMenuOnScroll =
    typeof hideHeaderMenuOnScrollProp === 'boolean'
      ? hideHeaderMenuOnScrollProp
      : hideHeaderMenuOnScrollFromStore;
  const dispatchHideHeaderMenuOnScroll = useCallback(
    value => dispatch(setHideHeaderMenuOnScrollAction(value)),
    [dispatch]
  );
  const setHideHeaderMenuOnScroll =
    setHideHeaderMenuOnScrollProp || dispatchHideHeaderMenuOnScroll;

  const [isMobileViewport, setIsMobileViewport] = useState(getIsMobileViewport);
  const [visible, setVisible] = useState(false);
  const [headerOffset, setHeaderOffset] = useState(0);
  const hasBeenVisibleRef = useRef(false);
  const lastScrollTopRef = useRef(0);
  const lastHideNavRef = useRef(hideHeaderMenuOnScroll);
  const hideHeaderMenuOnScrollRef = useRef(hideHeaderMenuOnScroll);

  hideHeaderMenuOnScrollRef.current = hideHeaderMenuOnScroll;

  const { productName: name, selectionText } = formatPdpSelectionLabel(
    productName,
    selectedCount,
    requiredCount
  );

  const setVisibleSafe = useCallback(nextVisible => {
    setVisible(prev => (prev === nextVisible ? prev : nextVisible));
  }, []);

  const setHeaderOffsetSafe = useCallback(nextOffset => {
    setHeaderOffset(prev => (prev === nextOffset ? prev : nextOffset));
  }, []);

  /**
   * Only dispatch when the hide-nav flag actually changes. Rapid true/false
   * oscillation (scroll jitter + body padding reflow) was freezing the PDP.
   */
  const setHideNavSafe = useCallback(
    nextHideNav => {
      if (lastHideNavRef.current === nextHideNav) {
        return;
      }
      lastHideNavRef.current = nextHideNav;
      setHideHeaderMenuOnScroll(nextHideNav);
    },
    [setHideHeaderMenuOnScroll]
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    const handleChange = event => {
      setIsMobileViewport(event.matches);
      if (!event.matches) {
        setVisibleSafe(false);
        hasBeenVisibleRef.current = false;
      }
    };

    setIsMobileViewport(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, [setVisibleSafe]);

  useEffect(() => {
    if (!isMobileViewport) {
      setHeaderOffsetSafe(0);
      return undefined;
    }

    let cancelled = false;
    let rafId = null;
    let rafId2 = null;
    const updateOffset = () => {
      if (cancelled) {
        return;
      }
      // When top nav is hidden (Menu-style), pin sticky under remaining chrome only.
      if (!hideHeaderMenuOnScrollRef.current) {
        setHeaderOffsetSafe(0);
        return;
      }
      setHeaderOffsetSafe(getSiteHeaderOffset());
    };

    updateOffset();
    // Nav remounts asynchronously after Redux flip — remeasure next frames so
    // sticky sits below the full banner, not just the ribbon.
    if (typeof window !== 'undefined' && window.requestAnimationFrame) {
      rafId = window.requestAnimationFrame(() => {
        updateOffset();
        rafId2 = window.requestAnimationFrame(updateOffset);
      });
    }

    window.addEventListener('resize', updateOffset);

    let resizeObserver = null;
    const banner =
      typeof document !== 'undefined'
        ? document.querySelector('[role="banner"]')
        : null;
    if (banner && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateOffset);
      resizeObserver.observe(banner);
    }

    return () => {
      cancelled = true;
      window.removeEventListener('resize', updateOffset);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (
        rafId != null &&
        typeof window !== 'undefined' &&
        window.cancelAnimationFrame
      ) {
        window.cancelAnimationFrame(rafId);
      }
      if (
        rafId2 != null &&
        typeof window !== 'undefined' &&
        window.cancelAnimationFrame
      ) {
        window.cancelAnimationFrame(rafId2);
      }
    };
    // Intentionally omit hideHeaderMenuOnScroll / visible: offset reads a ref so we
    // do not tear down ResizeObserver on every chrome toggle (freeze source).
  }, [isMobileViewport, setHeaderOffsetSafe]);

  // Remeasure once when chrome visibility flag flips (without resubscribing RO).
  useEffect(() => {
    if (!isMobileViewport) {
      return;
    }
    if (!hideHeaderMenuOnScroll) {
      setHeaderOffsetSafe(0);
      return;
    }
    setHeaderOffsetSafe(getSiteHeaderOffset());
  }, [hideHeaderMenuOnScroll, isMobileViewport, setHeaderOffsetSafe]);

  useEffect(() => {
    hasBeenVisibleRef.current = false;
    setVisibleSafe(false);

    if (!isMobileViewport) {
      return undefined;
    }

    const target = observeTargetRef && observeTargetRef.current;
    if (!target || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    // Seed from current geometry so rotate/resize or restored scroll still works.
    const rect = target.getBoundingClientRect();
    const viewportHeight =
      (typeof window !== 'undefined' && window.innerHeight) || 0;
    if (rect.bottom < 0) {
      // Title already scrolled fully above the viewport.
      hasBeenVisibleRef.current = true;
      setVisibleSafe(true);
    } else if (rect.top < viewportHeight && rect.bottom > 0) {
      // Title is currently on screen.
      hasBeenVisibleRef.current = true;
      setVisibleSafe(false);
    }

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (!entry) {
          return;
        }

        if (entry.isIntersecting) {
          // Title entered the viewport — remember it, keep sticky hidden.
          hasBeenVisibleRef.current = true;
          setVisibleSafe(false);
          return;
        }

        // Only show after the title was visible at least once, then scrolled past.
        // Prevents sticky appearing on first paint when the title starts below the fold.
        if (hasBeenVisibleRef.current) {
          setVisibleSafe(true);
        }
      },
      { threshold: 0, rootMargin: '0px' }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [observeTargetRef, observeKey, isMobileViewport, setVisibleSafe]);

  /*
   * Mimic Menu (Artur) scroll chrome:
   * - sticky "selected" banner visible + scroll down → hide top nav
   * - scroll up → show top nav again
   * - sticky hidden → restore top nav
   *
   * Guards below prevent Redux/layout thrash that froze cookie selection on mobile.
   */
  useEffect(() => {
    if (!isMobileViewport || !visible) {
      setHideNavSafe(true);
      return undefined;
    }

    lastScrollTopRef.current =
      (typeof window !== 'undefined' && window.pageYOffset) || 0;
    // Banner became visible because the user scrolled past the title — hide top nav.
    setHideNavSafe(false);

    const handleScroll = () => {
      const scrollTop =
        (typeof window !== 'undefined' && window.pageYOffset) || 0;
      const delta = scrollTop - lastScrollTopRef.current;
      if (Math.abs(delta) < SCROLL_DIRECTION_THRESHOLD_PX) {
        return;
      }
      const isScrollingDown = delta > 0;
      lastScrollTopRef.current = scrollTop;
      // hideHeaderMenuOnScroll=true means "show top nav" in this app.
      setHideNavSafe(!isScrollingDown);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      setHideNavSafe(true);
    };
  }, [visible, isMobileViewport, setHideNavSafe]);

  if (!isMobileViewport || !requiredCount || requiredCount <= 0) {
    return null;
  }

  return (
    <header
      className={`${styles.header} ${visible ? styles.headerVisible : ''}`}
      style={{ '--pdp-sticky-offset': `${headerOffset}px` }}
      data-testid="pdp-sticky-selection-header"
      aria-hidden={!visible}
    >
      <button
        type="button"
        className={styles.backButton}
        onClick={onBack}
        aria-label="Back to Menu"
        data-testid="pdp-sticky-selection-back"
      >
        <img src={backArrow} alt="" className={styles.backIcon} />
      </button>
      <p className={styles.title} data-testid="pdp-sticky-selection-title">
        <span className={styles.productName}>{name}</span>
        {/* Spacing via .title gap only — nbsp + gap looked like an extra space (Carlos) */}
        <span className={styles.selectionCount}>{selectionText}</span>
      </p>
      <div className={styles.rightSpacer} aria-hidden="true" />
    </header>
  );
};

PdpStickySelectionHeader.propTypes = {
  productName: PropTypes.string,
  selectedCount: PropTypes.number,
  requiredCount: PropTypes.number,
  onBack: PropTypes.func.isRequired,
  observeTargetRef: PropTypes.shape({ current: PropTypes.any }),
  observeKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hideHeaderMenuOnScroll: PropTypes.bool,
  setHideHeaderMenuOnScroll: PropTypes.func,
};

PdpStickySelectionHeader.defaultProps = {
  productName: '',
  selectedCount: 0,
  requiredCount: 0,
  observeTargetRef: null,
  observeKey: null,
  hideHeaderMenuOnScroll: undefined,
  setHideHeaderMenuOnScroll: undefined,
};

export { PdpStickySelectionHeader };
export default PdpStickySelectionHeader;
